import analytics, { GAEvents, GACategories, GAActions } from './analytics'
import {
  PageModel,
  CustomerTypeModel,
} from '../pages/PayApply/models/private-models'

const convertCustomerType = customerType => {
  if (customerType === CustomerTypeModel.NATURAL) {
    return 'Private'
  }
  if (customerType === CustomerTypeModel.LEGAL) {
    return 'Company'
  }
  return 'Unknown'
}

const getCustomerType = ({ paymentMethod, addressInfo }) => {
  const fromPaymentMethod = paymentMethod && paymentMethod.customerType
  const fromAddressInfo = addressInfo && addressInfo.customerType
  return convertCustomerType(fromPaymentMethod || fromAddressInfo)
}

export const sendPrintAgreementEvent = isPayment => {
  const label = isPayment
    ? 'Message: Print Payment Agreement'
    : 'Message: Print Application Agreement'
  analytics.sendEvent(
    GAEvents.APPLICATION_FLOW,
    GAActions.IMPRESSION,
    GACategories.applicationFlow.APPLICATION_STEPS,
    label,
  )
}

export const sendConfirmPaperAgreementSignedEvent = isPayment => {
  const label = isPayment
    ? 'Message: Confirmed Payment Agreement'
    : 'Message: Confirmed Application Agreement'
  analytics.sendEvent(
    GAEvents.APPLICATION_FLOW,
    GAActions.CONFIRM_PAPER_SIGN,
    GACategories.applicationFlow.APPLICATION_STEPS,
    label,
  )
}

export const sendTransactionSearchEvent = transactions => {
  analytics.sendEvent(
    GAEvents.MP_TRANSACTIONS,
    GAActions.SEARCH,
    GACategories.TRANSACTIONS,
    transactions.length ? 'Result' : 'No result',
  )
}

export const sendTransactionChangeFilterEvent = filter => {
  analytics.sendEvent(
    GAEvents.MP_TRANSACTIONS,
    GAActions.CHANGE_FILTER,
    GACategories.TRANSACTIONS,
    filter,
  )
}

export const sendChangeTransactionEvent = (action, transaction) => {
  if (action) {
    analytics.sendEvent(
      GAEvents.MP_TRANSACTIONS,
      GAActions.CHANGE_TRANSACTION,
      GACategories.TRANSACTIONS,
      `${action} - Status: ${transaction.payment.paymentStatus.status}`,
    )
  }
}

export const sendDebitConfirmationEvent = (state, transaction) => {
  if (state) {
    analytics.sendEvent(
      GAEvents.MP_TRANSACTIONS,
      GAActions.SEND_PAYMENT_CONFIRMATION,
      GACategories.TRANSACTIONS,
      `${state} - Status: ${transaction.payment.paymentStatus.status}`,
    )
  }
}

export const sendTransactionAddRowEvent = (state, transaction) => {
  if (state) {
    analytics.sendEvent(
      GAEvents.MP_TRANSACTIONS,
      GAActions.ADD_ROW,
      GACategories.TRANSACTIONS,
      `${state} - Status: ${transaction.payment.paymentStatus.status}`,
    )
  }
}

export const sendPaymentOptionEvent = (paymentMethod, action) => {
  if (paymentMethod) {
    analytics.sendEvent(
      GAEvents.APPLICATION_FLOW,
      action,
      GACategories.applicationFlow.PAYMENT_OPTIONS,
      paymentMethod.description,
      {
        dimension7: convertCustomerType(paymentMethod.customerType),
      },
    )
  }
}

export const sendDebitInMPEvent = (isDebit, paymentMethod) => {
  analytics.sendEvent(
    GAEvents.APPLICATION_FLOW,
    GAActions.DEBIT_IN_MP,
    GACategories.applicationFlow.PAYMENT_INFORMATION,
    isDebit ? 'Selected' : 'Not selected',
    {
      dimension7: getCustomerType({ paymentMethod }),
    },
  )
}

export const sendIdOrSigningEvent = (payApply, label) => {
  analytics.sendEvent(
    GAEvents.APPLICATION_FLOW,
    GAActions.PAY_APPLY_STEP_3,
    GACategories.applicationFlow.APPLICATION_STEPS,
    label,
    {
      dimension7: getCustomerType(payApply),
      dimension8:
        payApply.current && payApply.selectedPaymentMethod
          ? payApply.selectedPaymentMethod.description
          : 'okänd',
    },
  )
}

export const sendFinishedEvent = (payApply, label) => {
  analytics.sendEvent(
    GAEvents.APPLICATION_FLOW,
    GAActions.PAY_APPLY_STEP_4,
    GACategories.applicationFlow.APPLICATION_STEPS,
    label,
    {
      dimension7: getCustomerType({ addressInfo: payApply.addressInfo }),
      dimension8:
        payApply.current && payApply.selectedPaymentMethod
          ? payApply.selectedPaymentMethod.description
          : 'okänd',
    },
  )
}

export const sendStatisticFilterEvent = label => {
  analytics.sendEvent(
    GAEvents.MP_STATISTICS,
    GAActions.FILTER,
    GACategories.STATISTICS,
    label,
  )
}

export const sendChangeChartEvent = label => {
  analytics.sendEvent(
    GAEvents.MP_STATISTICS,
    GAActions.CHANGE_CHART,
    GACategories.STATISTICS,
    label,
  )
}

export const sendAddSingleUserEvent = label => {
  analytics.sendEvent(
    GAEvents.MP_ADD_NEW_USER,
    GAActions.ONE_USER,
    GACategories.ADD_NEW_USER,
    label,
  )
}

export const sendAddMultipleUsersEvent = label => {
  analytics.sendEvent(
    GAEvents.MP_ADD_NEW_USER,
    GAActions.MULTIPLE_USERS,
    GACategories.ADD_NEW_USER,
    label,
  )
}

export const sendChatImpressionEvent = () => {
  analytics.sendEvent(
    GAEvents.MP_CHAT_INTERACTION,
    GAActions.IMPRESSION,
    GACategories.CHAT_INTERACTION,
    'Chat popup',
  )
}

export const sendChatEvent = label => {
  analytics.sendEvent(
    GAEvents.MP_CHAT_INTERACTION,
    GAActions.START_CHAT,
    GACategories.CHAT_INTERACTION,
    label,
  )
}

export const sendLoginEvent = storeId => {
  analytics.sendEvent(
    GAEvents.MP_LOGIN,
    GAActions.LOGIN,
    GACategories.STORE_LOGIN,
    storeId,
  )
}

export const sendNextPageEvent = data => {
  switch (data.currentPage) {
    case PageModel.GOVERNMENT_ID:
      analytics.sendEvent(
        GAEvents.APPLICATION_FLOW,
        GAActions.PAY_APPLY_STEP_1,
        GACategories.applicationFlow.APPLICATION_STEPS,
        'Click next',
        {
          dimension7: convertCustomerType(data.customerType),
        },
      )
      break
    case PageModel.PAYMENT_METHOD:
      sendDebitInMPEvent(data.debitPaymentChecked, data.paymentMethod)
      analytics.sendEvent(
        GAEvents.APPLICATION_FLOW,
        GAActions.PAY_APPLY_STEP_2,
        GACategories.applicationFlow.APPLICATION_STEPS,
        'Click next',
        {
          dimension7: getCustomerType(data),
          dimension8: data.paymentMethod && data.paymentMethod.description,
        },
      )
      break
    case PageModel.PAYMENT_METHOD_FORM_SPEC:
      analytics.sendEvent(
        GAEvents.APPLICATION_FLOW,
        GAActions.PAY_APPLY_STEP_2_1,
        GACategories.applicationFlow.APPLICATION_STEPS,
        'Click next',
      )
      break
    default:
      break
  }
}
